import React from 'react';
import { Empty, Typography, Row, Col, Card, Button, Divider } from 'antd';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';

import { useUpdateEducationStepIdsAllowlist } from '../onboarding/useUpdateEducationStepIdsAllowlist';
import { useEntityRegistry } from '../useEntityRegistry';
import { ANTD_GRAY } from '../entity/shared/constants';
import {
    HOME_PAGE_DOMAINS_ID,
    HOME_PAGE_MOST_POPULAR_ID,
    HOME_PAGE_PLATFORMS_ID,
} from '../onboarding/config/HomePageOnboardingConfig';
import { PageRoutes } from '../../conf/Global';

import { RecommendationModule } from '../recommendations/RecommendationModule';
import { BrowseEntityCard } from '../search/BrowseEntityCard';
import { HomePagePosts } from './HomePagePosts';
import { DomainCards } from './DomainCards';

import { useListRecommendationsQuery } from '../../graphql/recommendations.generated';
import { useGetEntityCountsQuery } from '../../graphql/app.generated';
import {
    CorpUser,
    EntityType,
    RecommendationModule as RecommendationModuleType,
    RecommendationRenderType,
    ScenarioType,
} from '../../types.generated';
import { ReactComponent as IconNorthEast } from '../../images/icon-north_east.svg';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { AccessRestrictedBlock } from '../shared/AccessRestrictedBlock';
import { useUserContext } from '../context/useUserContext';
import { useTranslation } from '../../i18n/useTranslation';
import {
    getPrivilegeRestrictions,
    MOST_POPULAR_MODULE_ID,
    PLATFORMS_MODULE_ID,
    RECENTLY_EDITED_MODULE_ID,
    RECENTLY_VIEWED_MODULE_ID,
    TOP_TAGS_MODULE_ID,
    TOP_TERMS_MODULE_ID,
} from './utils';

const RecommendationContainer = styled.div`
    && {
        height: calc(100% - 24px);

        .ant-card {
            height: 100%;
        }
    }
`;

const BrowseCardContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    .stats-tile-card {
        height: auto;
        padding: 12px;
        border-radius: 2px;

        .tile-logo {
            top: 12px;
            right: 12px;
            width: 24px;
            height: 24px;
            display: flex;
            border-radius: 4px;
            align-items: center;
            justify-content: center;
            background-color: #eae8f4;

            svg,
            img {
                width: 16px;
                height: 16px;
                color: #7669ba;
            }
        }

        .tile-content {
            border-color: #7669ba;

            .tile-title {
                margin-top: 0;
            }
        }
    }
`;

const NoMetadataEmpty = styled(Empty)`
    font-size: 18px;
    color: ${ANTD_GRAY[8]};
`;

const NoMetadataContainer = styled.div`
    display: none;
`;

function getStepId(moduleId: string) {
    switch (moduleId) {
        case PLATFORMS_MODULE_ID:
            return HOME_PAGE_PLATFORMS_ID;
        case MOST_POPULAR_MODULE_ID:
            return HOME_PAGE_MOST_POPULAR_ID;
        default:
            return undefined;
    }
}

type Props = {
    user: CorpUser;
};

const simpleViewEntityTypes = [
    EntityType.Dataset,
    EntityType.Chart,
    EntityType.Dashboard,
    EntityType.GlossaryNode,
    EntityType.GlossaryTerm,
    EntityType.DataProduct,
];

export const HomePageRecommendations = ({ user }: Props) => {
    // Entity Types
    const entityRegistry = useEntityRegistry();
    const { t } = useTranslation();
    const { platformPrivileges } = useUserContext() ?? {};
    const { isDomainsRestricted, isPlatformsRestricted, isTopTagsRestricted, isTopGlossaryTermsRestricted } =
        getPrivilegeRestrictions(platformPrivileges);
    const browseEntityList = entityRegistry.getBrowseEntityTypes();
    const userUrn = user?.urn;

    const showSimplifiedHomepage = user?.settings?.appearance?.showSimplifiedHomepage;

    const { data: entityCountData, loading: entityCountLoading } = useGetEntityCountsQuery({
        variables: {
            input: {
                types: browseEntityList,
            },
        },
        errorPolicy: 'all',
    });

    const orderedEntityCounts = entityCountData?.getEntityCounts?.counts
        ?.sort((a, b) => {
            return browseEntityList.indexOf(a.entityType) - browseEntityList.indexOf(b.entityType);
        })
        .filter((entityCount) => !showSimplifiedHomepage || simpleViewEntityTypes.indexOf(entityCount.entityType) >= 0);

    // Recommendations
    const scenario = ScenarioType.Home;
    const { data, loading: recommendationLoading } = useListRecommendationsQuery({
        variables: {
            input: {
                userUrn,
                requestContext: {
                    scenario,
                },
                limit: 10,
            },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });
    const recommendationModules = data?.listRecommendations?.modules;

    // Determine whether metadata has been ingested yet.
    const hasIngestedMetadata =
        orderedEntityCounts && orderedEntityCounts.filter((entityCount) => entityCount.count > 0).length > 0;

    // we want to render the domain module first if it exists
    const domainRecommendationModule = recommendationModules?.find(
        (module) => module.renderType === RecommendationRenderType.DomainSearchList,
    );

    // Render domain onboarding step if the domains module exists
    const hasDomains = !!domainRecommendationModule;
    useUpdateEducationStepIdsAllowlist(hasDomains, HOME_PAGE_DOMAINS_ID);

    // Render platforms onboarding step if the platforms module exists
    const hasPlatforms = !!recommendationModules?.some((module) => module?.moduleId === PLATFORMS_MODULE_ID);
    useUpdateEducationStepIdsAllowlist(hasPlatforms, HOME_PAGE_PLATFORMS_ID);

    // Render most popular onboarding step if the most popular module exists
    const hasMostPopular = !!recommendationModules?.some((module) => module?.moduleId === MOST_POPULAR_MODULE_ID);
    useUpdateEducationStepIdsAllowlist(hasMostPopular, HOME_PAGE_MOST_POPULAR_ID);

    return (
        <>
            {(entityCountLoading || recommendationLoading) && <ComponentSpinner />}
            <div>
                <HomePagePosts />
                {orderedEntityCounts && orderedEntityCounts.length > 0 && (
                    <RecommendationContainer className="mb-1">
                        {domainRecommendationModule && (
                            <>
                                {hasIngestedMetadata ? (
                                    <BrowseCardContainer className="f-layout-vertical-border f-flex-column-xs">
                                        {orderedEntityCounts.map(
                                            (entityCount) =>
                                                entityCount.count !== 0 && (
                                                    <BrowseEntityCard
                                                        key={entityCount.entityType}
                                                        entityType={entityCount.entityType}
                                                        count={entityCount.count}
                                                    />
                                                ),
                                        )}
                                        {!orderedEntityCounts.some(
                                            (entityCount) => entityCount.entityType === EntityType.GlossaryTerm,
                                        ) && <BrowseEntityCard entityType={EntityType.GlossaryTerm} count={0} />}
                                    </BrowseCardContainer>
                                ) : (
                                    <NoMetadataContainer>
                                        <NoMetadataEmpty description={t('No Metadata Found 😢')} />
                                    </NoMetadataContainer>
                                )}
                                <Divider className="ma-0" />
                                {isDomainsRestricted ? (
                                    <AccessRestrictedBlock title={t('Domains')} />
                                ) : (
                                    <Card
                                        id={HOME_PAGE_DOMAINS_ID}
                                        className="f-dashboard-card-wrapper f-card-default"
                                        title={
                                            <>
                                                <Col flex="auto">
                                                    <Typography.Title level={4}>
                                                        {t(domainRecommendationModule.title)}
                                                    </Typography.Title>
                                                </Col>
                                                <Col flex="none">
                                                    <Link to={PageRoutes.DOMAINS}>
                                                        <Button type="text" className="f-btn-flat">
                                                            <span>{t('View All Domains')}</span>
                                                            <IconNorthEast />
                                                        </Button>
                                                    </Link>
                                                </Col>
                                            </>
                                        }
                                    >
                                        <DomainCards
                                            domainRecommendationModule={
                                                domainRecommendationModule as RecommendationModuleType
                                            }
                                        />
                                    </Card>
                                )}
                            </>
                        )}
                    </RecommendationContainer>
                )}
                <Row className="f-recommendation-cards-wrapper">
                    {recommendationModules &&
                        recommendationModules
                            .filter((module) => module.renderType !== RecommendationRenderType.DomainSearchList)
                            .map((module) => (
                                <Col
                                    xs={24}
                                    key={module.moduleId}
                                    id={getStepId(module.moduleId)}
                                    lg={module.moduleId === PLATFORMS_MODULE_ID ? 24 : 12}
                                >
                                    {(module.moduleId === TOP_TERMS_MODULE_ID && isTopGlossaryTermsRestricted) ||
                                    (module.moduleId === TOP_TAGS_MODULE_ID && isTopTagsRestricted) ||
                                    (module.moduleId === PLATFORMS_MODULE_ID && isPlatformsRestricted) ? (
                                        <AccessRestrictedBlock title={t(module.title)} />
                                    ) : (
                                        <Card
                                            title={
                                                <>
                                                    <Col flex="auto">
                                                        <Typography.Title level={4}>{t(module.title)}</Typography.Title>
                                                    </Col>
                                                    {
                                                        // Only show the view all button for glossary terms
                                                        module.moduleId === TOP_TERMS_MODULE_ID && (
                                                            <Col flex="none">
                                                                <Link to={PageRoutes.GLOSSARY}>
                                                                    <Button type="text" className="f-btn-flat">
                                                                        <span>{t('View All Glossary Terms')}</span>
                                                                        <IconNorthEast />
                                                                    </Button>
                                                                </Link>
                                                            </Col>
                                                        )
                                                    }
                                                </>
                                            }
                                            className={`f-dashboard-card-wrapper fill-height ${
                                                module.moduleId === PLATFORMS_MODULE_ID && 'f-card-default'
                                            } ${
                                                module.moduleId === MOST_POPULAR_MODULE_ID ||
                                                module.moduleId === RECENTLY_EDITED_MODULE_ID ||
                                                module.moduleId === RECENTLY_VIEWED_MODULE_ID
                                                    ? 'f-recent-most-viewed-card-wrapper'
                                                    : ''
                                            }`}
                                        >
                                            <RecommendationModule
                                                key={module.moduleId}
                                                module={module as RecommendationModuleType}
                                                scenarioType={scenario}
                                                showTitle={false}
                                            />
                                        </Card>
                                    )}
                                </Col>
                            ))}
                </Row>
            </div>
        </>
    );
};
